import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/api/client';
import { API_BANK_PAYMENTS_START } from '@/api/endpoints';
import { POST_PAYMENT_PARAM, ROUTE_HOME } from '@/router/routes';
import { PaymentOptionType } from '@/types/components';
import type { PaymentMethod } from '@/types/components';
import { unwrapAxiosResponse } from '@/utils/axios';
import { toApiDate } from '@/utils/date';
import { ErrorMessage, logRequestError } from '@/utils/errors';

interface BankPaymentResponse {
  link: string;
}

interface StartBankPaymentParams {
  paymentMethod: PaymentMethod;
  paymentOptionType: PaymentOptionType;
  recurringPaymentEndDate: Date;
}

interface Params {
  onError: () => void;
}

export const useStartBankPayment = ({ onError }: Params) => {
  const {
    mutate: startBankPayment,
    isPending,
    isSuccess,
    variables,
    reset,
  } = useMutation({
    mutationFn: ({
      paymentMethod,
      paymentOptionType,
      recurringPaymentEndDate,
    }: StartBankPaymentParams) =>
      apiClient
        .post<BankPaymentResponse>(API_BANK_PAYMENTS_START, {
          paymentMethod,
          paymentOption: paymentOptionType,
          postPaymentReturnLink: `${window.location.origin}${ROUTE_HOME}?${POST_PAYMENT_PARAM}=true`,
          isRecurringPayment: paymentOptionType === PaymentOptionType.Recurring,
          recurringPaymentEndDate: toApiDate(recurringPaymentEndDate),
        })
        .then(unwrapAxiosResponse),
    onSuccess: ({ link }) => {
      window.location.assign(link);
    },
    onError: (error) => {
      logRequestError(ErrorMessage.StartBankPayment, error);
      onError();
    },
  });

  const isRedirecting = isPending || isSuccess;
  const pendingMethod = isRedirecting ? variables.paymentMethod : undefined;

  useEffect(() => {
    const handlePageShow = ({ persisted }: PageTransitionEvent) => {
      if (persisted) {
        reset();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [reset]);

  return { startBankPayment, pendingMethod };
};
